/**
 * Central navigation config — SINGLE SOURCE OF TRUTH.
 *
 * PARITY RULE (enforced by test_navigation_parity):
 *   Every entry here MUST render in both desktop pill and mobile drawer.
 *   Adding a new room? Add it here + a matching testid in constants/testIds.js.
 *   Ship desktop + mobile in the same commit — do not create a mobile gap.
 */
import {
    FlaskConical, GraduationCap, Gamepad2, Wrench, Users, Sparkles,
    ShoppingBag, ScrollText, BookOpen, Compass, Briefcase, ShieldCheck, Server, Globe, Handshake,
} from 'lucide-react';
import { NAV, ARCHIVE, ROADMAP } from '@/constants/testIds';

// Primary rooms — full room list (mobile drawer + room registry source of truth).
export const PRIMARY_ROOMS = [
    { to: '/',           label: 'Facility',  icon: FlaskConical, testid: NAV.linkLab,        code: '00' },
    { to: '/academy',    label: 'Academy',   icon: GraduationCap, testid: 'nav-link-academy-room', code: '01' },
    { to: '/library',    label: 'Archive/FAQ', icon: BookOpen,   testid: 'nav-link-archive-faq', code: '06' },
    { to: '/play',       label: 'Modules',   icon: Gamepad2,     testid: NAV.linkPlay,       code: '02' },
    { to: '/build',      label: 'Build',     icon: Wrench,       testid: NAV.linkBuild,      code: '03' },
    { to: '/network',    label: 'Network',   icon: Users,        testid: NAV.linkCommunity,  code: '04' },
    { to: '/shop',       label: 'Market',    icon: ShoppingBag,  testid: NAV.linkShop,       code: '05' },
    { to: '/automation-center', label: 'Automation Center', icon: Briefcase, testid: NAV.linkHub, code: '10' },
    { to: '/web-development', label: 'Web Dev', icon: Globe, testid: 'nav-link-webdev', code: '13' },
    { to: '/digital-models', label: 'Digital Models', icon: Users, testid: 'nav-link-models', code: '14' },
    { to: '/verify',     label: 'Verify',    icon: ShieldCheck,  testid: NAV.linkVerify,     code: '11' },
    { to: '/membership', label: 'Clearance', icon: Sparkles,     testid: NAV.linkMembership, code: '08' },
    { to: '/work-with-us', label: 'Partners & Contractors', icon: Handshake, testid: 'nav-link-work-with-us', code: '15' },
    { to: '/about', label: 'About', icon: Users, testid: 'nav-link-about', code: '16' },
];

const _byCode = (code) => PRIMARY_ROOMS.find((r) => r.code === code);

// Desktop nav structure — grouped so the pill bar stays readable.
// Every grouped room still renders flat in the mobile drawer (parity rule).
export const DESKTOP_NAV = [
    { type: 'link', room: _byCode('00') },
    {
        type: 'group', id: 'services', label: 'Services', icon: Briefcase, testid: 'nav-group-services',
        rooms: [_byCode('10'), _byCode('13'), _byCode('14'), _byCode('03'), _byCode('05')],
    },
    { type: 'link', room: _byCode('11') },
    { type: 'academy' },
    {
        type: 'group', id: 'community', label: 'Community', icon: Users, testid: 'nav-group-community',
        rooms: [
            _byCode('04'), _byCode('08'),
            { to: '/labs/server-farm', label: 'Server Labs', icon: Server, testid: NAV.linkLabs, code: '09' },
            _byCode('15'),
            _byCode('16'),
        ],
    },
];

// Academy umbrella — free-tier rooms grouped together.
// Renders as a desktop dropdown and as an "ACADEMY · FREE ACCESS" section in the mobile drawer.
export const ACADEMY_ROOMS = [
    { to: '/academy',  code: '01', label: 'The Academy',          subtitle: 'Observation deck · orientation & training overview',
      icon: GraduationCap, color: '#00F0FF', testid: 'nav-academy-deck', panelTestid: 'archive-link-academy-deck', badge: 'FREE' },
    { to: '/learn',    code: '01', label: 'Learning Pathways',   subtitle: 'The Academy · investigator training constellation',
      icon: GraduationCap, color: '#00F0FF', testid: NAV.linkLearn,      panelTestid: ARCHIVE.linkLearn,      badge: 'FREE' },
    { to: '/play',     code: '02', label: 'Modules',              subtitle: 'Training modules & simulations',
      icon: Gamepad2,     color: '#00FF66', testid: 'nav-academy-modules', panelTestid: 'archive-link-modules', badge: 'FREE' },
    { to: '/play#free-drill', code: '02', label: 'Free Field Drill', subtitle: 'Neural Field Drill · playable now · no signup',
      icon: Gamepad2,     color: '#00FF66', testid: NAV.linkFreeDrill,  panelTestid: ARCHIVE.linkFreeDrill,  badge: 'FREE' },
    { to: '/library',  code: '06', label: 'Archive / FAQ', subtitle: 'Public knowledge archive + FAQ · free · no signup',
      icon: BookOpen,     color: '#00F0FF', testid: NAV.linkLibrary,    panelTestid: ARCHIVE.linkLibrary,    badge: 'FREE' },
    { to: '/log',      code: '07', label: 'Case Files',           subtitle: "Founder's Log · declassified transmissions",
      icon: ScrollText,   color: '#8DA0B8', testid: NAV.linkLog,        panelTestid: ARCHIVE.linkLog,        badge: 'FREE' },
    { to: '/roadmap',  code: '12', label: 'The Roadmap',          subtitle: 'Where S.M.I. is headed · phased vision',
      icon: Compass,      color: '#B026FF', testid: ROADMAP.navLink,    panelTestid: ROADMAP.archiveLink,    badge: 'PUBLIC' },
];

// Future Labs — rendered after the Academy trigger on desktop and in the mobile ROOMS list.
export const LABS_ROOM = {
    to: '/labs/server-farm', label: 'Server Labs', icon: Server, testid: NAV.linkLabs, code: '09',
};

// Room registry — SINGLE SOURCE OF TRUTH for "how many rooms exist".
// Home stats and the footer room list derive from this; never hardcode counts.
const _roomSeen = new Map();
[...PRIMARY_ROOMS, ...ACADEMY_ROOMS, LABS_ROOM].forEach((r) => {
    if (!_roomSeen.has(r.code)) _roomSeen.set(r.code, { code: r.code, label: r.label, to: r.to });
});
export const ROOM_REGISTRY = [..._roomSeen.values()].sort((a, b) => a.code.localeCompare(b.code));

/**
 * Every route the primary nav offers (excluding auth destinations).
 * The mobile parity test asserts each testid is present in the drawer DOM.
 */
export const ALL_NAV_TESTIDS = [
    ...PRIMARY_ROOMS.map((r) => r.testid),
    ...ACADEMY_ROOMS.map((r) => r.testid),
    LABS_ROOM.testid,
];

// Auth-related testids the mobile drawer must expose in each auth state.
export const MOBILE_AUTH_TESTIDS = {
    signedOut:  ['mobile-auth-signin'],
    signedIn:   ['mobile-auth-desk', 'mobile-auth-signout'],
    adminExtra: ['mobile-auth-admin'],
};
